import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  output,
  PLATFORM_ID
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import intlTelInput, { AllOptions, Iso2, Iti, SelectedCountry } from 'intl-tel-input/intlTelInputWithUtils';

@Directive({
  selector: '[libNg2TelInput]',
  standalone: true,
})
export class Ng2TelInput implements OnInit, OnDestroy {
  @Input() ng2TelInputOptions: Partial<AllOptions> = {};
  readonly hasError = output<boolean>();
  readonly ng2TelOutput = output<string | undefined>();
  readonly countryChange = output<SelectedCountry | undefined>();
  readonly intlTelInputObject = output<Iti>();

  ngTelInput?: Iti;

  private el = inject(ElementRef);
  private platformId = inject(PLATFORM_ID);

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.ngTelInput = intlTelInput(this.el.nativeElement, {
        loadUtils: () => import('intl-tel-input/utils'),
        dropdownParent: document.body,
        countrySelectorMode: 'AUTO',
        countryNameLocale: 'fr',
        countryNameOverrides: {
          fr: 'France',
          es: 'Espagne (Spain, España)',
          it: 'Italie (Italy, Italia)',
          de: 'Allemagne (Germany, Deutschland)',
          gb: 'Royaume-Uni (United Kingdom)',
        },
        initialCountry: 'fr',
        ...this.ng2TelInputOptions,
      });

      this.el.nativeElement.addEventListener(
        'countrychange',
        this.onCountryChange
      );

      this.intlTelInputObject.emit(this.ngTelInput);
    }
  }

  ngOnDestroy() {
    if (this.ngTelInput) {
      this.el.nativeElement.removeEventListener(
        'countrychange',
        this.onCountryChange
      );
      this.ngTelInput.destroy();
    }
  }

  @HostListener('blur') onBlur() {
    const isInputValid: boolean = this.isInputValid();
    if (isInputValid) {
      const telOutput = this.ngTelInput?.getNumber();
      this.hasError.emit(isInputValid);
      this.ng2TelOutput.emit(telOutput);
    } else {
      this.hasError.emit(isInputValid);
    }
  }

  isInputValid(): boolean {
    return this.ngTelInput?.isValidNumber() ?? false;
  }

  setCountry(country: Iso2) {
    this.ngTelInput?.setSelectedCountry(country);
  }

  private onCountryChange = () => {
    this.countryChange.emit(this.ngTelInput?.getSelectedCountry());
  };
}
